import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaEllipsisH } from "react-icons/fa";

import { BreadcrumbWrap } from "components/layouts/app-layout/styles/app-layout.styles";
import {
  AppLayoutBreadcrumbCurrent,
  AppLayoutBreadcrumbEllipsis,
  AppLayoutBreadcrumbLink,
  AppLayoutBreadcrumbMenu,
  AppLayoutBreadcrumbMenuLink,
  AppLayoutBreadcrumbSeparator,
  AppLayoutBreadcrumbTrigger,
  AppLayoutFaChevronDown,
  AppLayoutFaChevronRight,
  AppLayoutListItem,
  AppLayoutMeasureList,
  AppLayoutOrderedList,
} from "components/layouts/app-layout/styles/app-layout.inline.styles";
import { planBreadcrumbCollapse } from "utils/breadcrumb-collapse";
import { BreadcrumbChild, BreadcrumbItem } from "utils/breadcrumbs";

type AppBreadcrumbProps = {
  items: BreadcrumbItem[];
  onNavigate: (href: string) => void;
};

type VisibleSlot = { item: BreadcrumbItem; type: "item" } | { type: "ellipsis" };

type MenuDestination = {
  href: string;
  label: string;
};

const allVisiblePlan = (count: number) => ({
  ellipsisAt: null as number | null,
  hiddenIndices: [] as number[],
  visibleIndices: Array.from({ length: count }, (_, index) => index),
});

const isPlainLeftClick = (event: React.MouseEvent) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

const handleHrefNavigation = (
  event: React.MouseEvent<HTMLAnchorElement>,
  href: string,
  onNavigate: (href: string) => void,
  onAfter?: () => void,
) => {
  if (!isPlainLeftClick(event)) {
    return;
  }

  event.preventDefault();
  onAfter?.();
  onNavigate(href);
};

const wrapIndex = (index: number, length: number) => {
  if (length <= 0) {
    return 0;
  }

  return (index + length) % length;
};

const useDismissable = (
  isOpen: boolean,
  onClose: () => void,
  rootRef: React.RefObject<HTMLElement>,
  restoreFocusRef?: React.RefObject<HTMLElement>,
) => {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target;

      if (!(target instanceof Node) || rootRef.current?.contains(target)) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      onClose();
      restoreFocusRef?.current?.focus();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, restoreFocusRef, rootRef]);
};

const BreadcrumbMenu: React.FC<{
  focusOnOpen: "first" | "last";
  id: string;
  items: MenuDestination[];
  onClose: () => void;
  onNavigate: (href: string) => void;
}> = ({ focusOnOpen, id, items, onClose, onNavigate }) => {
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const focusItem = useCallback((index: number) => {
    const nextIndex = wrapIndex(index, itemRefs.current.length);
    setActiveIndex(nextIndex);
    itemRefs.current[nextIndex]?.focus();
  }, []);

  useLayoutEffect(() => {
    const nextIndex = focusOnOpen === "last" ? items.length - 1 : 0;
    setActiveIndex(Math.max(0, nextIndex));
    itemRefs.current[Math.max(0, nextIndex)]?.focus();
  }, [focusOnOpen, items.length]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusItem(activeIndex + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusItem(activeIndex - 1);
        break;
      case "Home":
        event.preventDefault();
        focusItem(0);
        break;
      case "End":
        event.preventDefault();
        focusItem(items.length - 1);
        break;
      case "Tab":
        onClose();
        break;
      default:
        break;
    }
  };

  return (
    <AppLayoutBreadcrumbMenu id={id} onKeyDown={handleKeyDown} role="menu">
      {items.map((item, index) => (
        <AppLayoutBreadcrumbMenuLink
          href={item.href}
          key={item.href}
          onClick={(event) => handleHrefNavigation(event, item.href, onNavigate, onClose)}
          ref={(node) => {
            itemRefs.current[index] = node;
          }}
          role="menuitem"
          tabIndex={index === activeIndex ? 0 : -1}
        >
          {item.label}
        </AppLayoutBreadcrumbMenuLink>
      ))}
    </AppLayoutBreadcrumbMenu>
  );
};

const CrumbLabel: React.FC<{
  item: BreadcrumbItem;
  onNavigate: (href: string) => void;
}> = ({ item, onNavigate }) => {
  const [isOpen, setOpen] = useState(false);
  const [focusOnOpen, setFocusOnOpen] = useState<"first" | "last">("first");
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => setOpen(false), []);
  const childItems = item.children || [];

  useDismissable(isOpen, closeMenu, menuRef, triggerRef);

  if (item.isCurrent) {
    return (
      <AppLayoutBreadcrumbCurrent aria-current="page">{item.label}</AppLayoutBreadcrumbCurrent>
    );
  }

  if (!childItems.length) {
    return (
      <AppLayoutBreadcrumbLink
        href={item.href}
        onClick={(event) => handleHrefNavigation(event, item.href, onNavigate)}
      >
        {item.label}
      </AppLayoutBreadcrumbLink>
    );
  }

  const menuItems: MenuDestination[] = [
    { href: item.href, label: item.label },
    ...childItems.map((child: BreadcrumbChild) => ({ href: child.href, label: child.label })),
  ];

  return (
    <div ref={menuRef}>
      <AppLayoutBreadcrumbTrigger
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => {
          setFocusOnOpen("first");
          setOpen((open) => !open);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setFocusOnOpen("first");
            setOpen(true);
          }

          if (event.key === "ArrowUp") {
            event.preventDefault();
            setFocusOnOpen("last");
            setOpen(true);
          }
        }}
        ref={triggerRef}
        type="button"
      >
        {item.label}
        <AppLayoutFaChevronDown />
      </AppLayoutBreadcrumbTrigger>
      {isOpen ? (
        <BreadcrumbMenu
          focusOnOpen={focusOnOpen}
          id={menuId}
          items={menuItems}
          onClose={closeMenu}
          onNavigate={onNavigate}
        />
      ) : null}
    </div>
  );
};

const buildVisibleSlots = (
  items: BreadcrumbItem[],
  visibleIndices: number[],
  ellipsisAt: number | null,
): VisibleSlot[] => {
  const slots: VisibleSlot[] = [];

  visibleIndices.forEach((itemIndex, visibleIndex) => {
    if (ellipsisAt === visibleIndex) {
      slots.push({ type: "ellipsis" });
    }

    const item = items[itemIndex];

    if (item) {
      slots.push({ item, type: "item" });
    }
  });

  return slots;
};

export const AppBreadcrumb: React.FC<AppBreadcrumbProps> = ({ items, onNavigate }) => {
  const containerRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLOListElement>(null);
  const overflowRef = useRef<HTMLLIElement>(null);
  const ellipsisRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const [isOverflowOpen, setOverflowOpen] = useState(false);
  const [focusOnOpen, setFocusOnOpen] = useState<"first" | "last">("first");
  const [plan, setPlan] = useState(() => allVisiblePlan(items.length));
  const closeOverflow = useCallback(() => setOverflowOpen(false), []);

  const hiddenItems = useMemo(
    () => plan.hiddenIndices.map((index) => items[index]).filter(Boolean),
    [items, plan.hiddenIndices],
  );
  const visibleSlots = useMemo(
    () => buildVisibleSlots(items, plan.visibleIndices, plan.ellipsisAt),
    [items, plan.ellipsisAt, plan.visibleIndices],
  );

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measureRoot = measureRef.current;

    if (!container || !measureRoot) {
      return;
    }

    const itemNodes = Array.from(measureRoot.querySelectorAll<HTMLElement>("[data-measure-item]"));
    const separatorNode = measureRoot.querySelector<HTMLElement>("[data-measure-separator]");
    const ellipsisNode = measureRoot.querySelector<HTMLElement>("[data-measure-ellipsis]");
    const itemWidths = itemNodes.map((node) => node.getBoundingClientRect().width);
    const nextPlan = planBreadcrumbCollapse(
      itemWidths,
      container.getBoundingClientRect().width,
      ellipsisNode?.getBoundingClientRect().width || 0,
      separatorNode?.getBoundingClientRect().width || 0,
    );

    setPlan((current) => {
      if (
        current.ellipsisAt === nextPlan.ellipsisAt &&
        current.hiddenIndices.join() === nextPlan.hiddenIndices.join() &&
        current.visibleIndices.join() === nextPlan.visibleIndices.join()
      ) {
        return current;
      }

      return nextPlan;
    });
  }, []);

  useLayoutEffect(() => {
    setPlan(allVisiblePlan(items.length));
    setOverflowOpen(false);
  }, [items]);

  useLayoutEffect(() => {
    recompute();
  }, [items, recompute]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => recompute());
    observer.observe(container);

    return () => observer.disconnect();
  }, [recompute]);

  useDismissable(isOverflowOpen, closeOverflow, overflowRef, ellipsisRef);

  const openOverflow = (nextFocus: "first" | "last") => {
    setFocusOnOpen(nextFocus);
    setOverflowOpen(true);
  };

  return (
    <BreadcrumbWrap aria-label="Breadcrumb" ref={containerRef}>
      <AppLayoutMeasureList aria-hidden="true" ref={measureRef}>
        {items.map((item) => (
          <AppLayoutListItem data-measure-item key={`measure-${item.href}`}>
            {item.isCurrent ? (
              <AppLayoutBreadcrumbCurrent>{item.label}</AppLayoutBreadcrumbCurrent>
            ) : (
              <AppLayoutBreadcrumbLink href={item.href} tabIndex={-1}>
                {item.label}
              </AppLayoutBreadcrumbLink>
            )}
          </AppLayoutListItem>
        ))}
        <AppLayoutListItem data-measure-separator>
          <AppLayoutBreadcrumbSeparator>
            <AppLayoutFaChevronRight />
          </AppLayoutBreadcrumbSeparator>
        </AppLayoutListItem>
        <AppLayoutListItem data-measure-ellipsis>
          <AppLayoutBreadcrumbEllipsis tabIndex={-1} type="button">
            <FaEllipsisH />
          </AppLayoutBreadcrumbEllipsis>
        </AppLayoutListItem>
      </AppLayoutMeasureList>

      <AppLayoutOrderedList>
        {visibleSlots.map((slot, slotIndex) => {
          if (slot.type === "ellipsis") {
            return (
              <AppLayoutListItem key="breadcrumb-ellipsis" ref={overflowRef}>
                {slotIndex > 0 ? (
                  <AppLayoutBreadcrumbSeparator aria-hidden="true">
                    <AppLayoutFaChevronRight />
                  </AppLayoutBreadcrumbSeparator>
                ) : null}
                <AppLayoutBreadcrumbEllipsis
                  aria-controls={isOverflowOpen ? menuId : undefined}
                  aria-expanded={isOverflowOpen}
                  aria-haspopup="menu"
                  aria-label="More breadcrumb links"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (isOverflowOpen) {
                      closeOverflow();
                      return;
                    }

                    openOverflow("first");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowDown") {
                      event.preventDefault();
                      openOverflow("first");
                    }

                    if (event.key === "ArrowUp") {
                      event.preventDefault();
                      openOverflow("last");
                    }
                  }}
                  ref={ellipsisRef}
                  type="button"
                >
                  <FaEllipsisH />
                </AppLayoutBreadcrumbEllipsis>
                {isOverflowOpen ? (
                  <BreadcrumbMenu
                    focusOnOpen={focusOnOpen}
                    id={menuId}
                    items={hiddenItems}
                    onClose={closeOverflow}
                    onNavigate={onNavigate}
                  />
                ) : null}
              </AppLayoutListItem>
            );
          }

          return (
            <AppLayoutListItem key={slot.item.href}>
              {slotIndex > 0 ? (
                <AppLayoutBreadcrumbSeparator aria-hidden="true">
                  <AppLayoutFaChevronRight />
                </AppLayoutBreadcrumbSeparator>
              ) : null}
              <CrumbLabel item={slot.item} onNavigate={onNavigate} />
            </AppLayoutListItem>
          );
        })}
      </AppLayoutOrderedList>
    </BreadcrumbWrap>
  );
};
